const FILTERED = '[Filtered]';

type JsonRecord = Record<string, unknown>;

function isRecord(value: unknown): value is JsonRecord {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function normalizeKey(key: string): string {
  return key.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/** True for auth, cookie, password, token, and card/payment field names. */
export function isSensitiveKey(key: string): boolean {
  const norm = normalizeKey(key);
  if (!norm) return false;

  if (
    norm === 'authorization' ||
    norm === 'proxyauthorization' ||
    norm === 'cookie' ||
    norm === 'cookies' ||
    norm === 'setcookie' ||
    norm === 'password' ||
    norm === 'passwd' ||
    norm === 'pwd' ||
    norm === 'token' ||
    norm === 'secret' ||
    norm === 'apikey' ||
    norm === 'xdebugkey' ||
    norm === 'card' ||
    norm === 'cardnumber' ||
    norm === 'cardno' ||
    norm === 'creditcard' ||
    norm === 'debitcard' ||
    norm === 'pan' ||
    norm === 'cvv' ||
    norm === 'cvc' ||
    norm === 'cvc2' ||
    norm === 'payment' ||
    norm === 'paymentmethod'
  ) {
    return true;
  }

  if (
    norm.endsWith('password') ||
    norm.endsWith('passwd') ||
    norm.endsWith('token') ||
    norm.endsWith('secret') ||
    norm.endsWith('apikey') ||
    norm.endsWith('debugkey') ||
    norm.endsWith('cardnumber') ||
    norm.endsWith('creditcard') ||
    norm.endsWith('debitcard') ||
    norm.endsWith('cvv') ||
    norm.endsWith('cvc')
  ) {
    return true;
  }

  if (norm.includes('authorization')) return true;
  if (norm.endsWith('cookie') || norm.endsWith('cookies')) return true;
  if (norm.includes('payment')) return true;
  if (norm.includes('cardnumber') || norm.includes('creditcard') || norm.includes('debitcard')) return true;
  if (norm.includes('cardholder') || norm.includes('nameoncard')) return true;

  return false;
}

function scrubUrl(value: string): string {
  if (!value.includes('?') && !value.includes('&')) return value;
  return value.replace(/([?&])([^=&#\s]+)=([^&#\s]*)/g, (match, sep, key) => {
    if (!isSensitiveKey(key)) return match;
    return `${sep}${key}=${FILTERED}`;
  });
}

function scrubString(value: string): string {
  let out = value.replace(/\bBearer\s+[A-Za-z0-9\-._~+/]+=*/gi, 'Bearer [Filtered]');
  out = out.replace(
    /((?:authorization|password|passwd|pwd|token|secret|api[_-]?key|cvv|cvc|pan|card[_-]?number|credit[_-]?card|payment(?:[_-]?method)?)\s*[=:]\s*)(?:"[^"]*"|'[^']*'|[^\s&]+)/gi,
    '$1[Filtered]'
  );
  return scrubUrl(out);
}

function scrubInPlace(value: unknown, seen: WeakSet<object>): void {
  if (!value || typeof value !== 'object') return;
  if (seen.has(value)) return;
  seen.add(value);

  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i += 1) {
      value[i] = scrubNode(value[i], seen);
    }
    return;
  }

  if (!isRecord(value)) return;
  for (const key of Object.keys(value)) {
    if (isSensitiveKey(key)) {
      value[key] = FILTERED;
      continue;
    }
    value[key] = scrubNode(value[key], seen);
  }
}

function scrubNode(value: unknown, seen: WeakSet<object>): unknown {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (
      (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
      (trimmed.startsWith('[') && trimmed.endsWith(']'))
    ) {
      try {
        const parsed: unknown = JSON.parse(value);
        scrubInPlace(parsed, seen);
        return JSON.stringify(parsed);
      } catch {
        /* not JSON */
      }
    }
    return scrubString(value);
  }
  if (!value || typeof value !== 'object') return value;
  scrubInPlace(value, seen);
  return value;
}

/**
 * Remove secrets from a Sentry error event. Drops non-error event types.
 */
export function scrubSentryEvent<T extends object>(event: T): T | null {
  if (!event || typeof event !== 'object') return event;
  const record = event as T & { type?: unknown };
  if (typeof record.type === 'string' && record.type.length > 0) return null;

  scrubInPlace(event, new WeakSet());

  if (isRecord(event)) {
    const request = event.request;
    if (isRecord(request) && request.cookies != null) {
      request.cookies = FILTERED;
    }
    const user = event.user;
    if (isRecord(user)) {
      delete user.email;
      delete user.ip_address;
      delete user.username;
      delete user.ip;
    }
  }

  return event;
}

export { FILTERED };
